import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Heart, MessageCircle, Share2, Play, Pause, CheckCircle2, Volume2, VolumeX,
  Search, Bookmark, UserCheck, X, Send, Sparkles, Film, LoaderCircle, ArrowDown
} from 'lucide-react';
import { sampleShorts } from '../../mockData';
import { ShortVideo, MediaHistoryItem, AppUser } from '../../types';
import {
  incrementMediaCount, likeMediaOnce, followUser, unfollowUser, subscribeToFollowStatus,
  subscribeToMediaFeed, formatCount
} from '../../backend/mediaService';
import { InstagramComments } from '../shared/components/InstagramComments';
import { ClueBadge } from '../shared/components/ClueBadge';
import { shareContent } from '../../backend/sharingService';
import { markCacheActive, releaseCacheActive } from '../../backend/cacheService';
import { Skeleton } from '../shared/components/Skeleton';
import { ContentActionsMenu } from '../shared/components/ContentActionsMenu';
import { subscribeToBlockedUsers } from '../../backend/moderationService';
import { collection, query, limit, getDocs, startAfter, QueryDocumentSnapshot, orderBy, where } from 'firebase/firestore';
import { db } from '../../backend/firebase';
import { prioritizeAndRandomizeFeed } from '../../utils/feedPrioritizer';
import { PullToRefresh } from '../shared/components/PullToRefresh';

interface MiniSTabProps {
  mediaHistory?: MediaHistoryItem[];
  onOpenSubscribeModal?: (creatorName: string, creatorUid?: string) => void;
  onOpenAuth?: () => void;
  currentUser?: AppUser | null;
  highlightedMedia?: MediaHistoryItem;
  onOpenProfile?: (uid: string) => void;
}

export function ShortsTab({ mediaHistory = [], onOpenAuth, currentUser, highlightedMedia, onOpenProfile }: MiniSTabProps) {
  const [shortsList, setShortsList] = useState<ShortVideo[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [lastDoc, setLastDoc] = useState<QueryDocumentSnapshot | null>(null);
  const [totalShortsCount, setTotalShortsCount] = useState(0);
  const [blockedUserIds, setBlockedUserIds] = useState<Set<string>>(new Set());
  const [blockedUsersReady, setBlockedUsersReady] = useState(!currentUser?.uid);
  const [blockedUsersError, setBlockedUsersError] = useState('');

  const [isMuted, setIsMuted] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!currentUser?.uid) {
      setBlockedUserIds(new Set());
      setBlockedUsersReady(true);
      return;
    }
    setBlockedUsersReady(false);
    setBlockedUsersError('');
    return subscribeToBlockedUsers(currentUser.uid, blocked => {
      setBlockedUserIds(blocked);
      setBlockedUsersReady(true);
    }, error => {
      setBlockedUsersError(error.message);
      setBlockedUsersReady(true);
    });
  }, [currentUser?.uid]);

  const followingUidsRef = useRef<Set<string>>(new Set());
  useEffect(() => {
    if (!currentUser?.uid) {
      followingUidsRef.current = new Set();
      return;
    }
    let active = true;
    void getDocs(collection(db, 'users', currentUser.uid, 'following'))
      .then(snap => {
        if (!active) return;
        const uids = new Set<string>();
        snap.docs.forEach(doc => {
          const uid = doc.data().uid || doc.id;
          if (uid) uids.add(uid);
        });
        followingUidsRef.current = uids;
      })
      .catch(err => console.warn('Could not load following set for MiniS:', err));
    return () => { active = false; };
  }, [currentUser?.uid]);

  // 1. PART 3: Pagination (limit(10) + startAfter)
  const fetchShortsBatch = async (isRefresh = false) => {
    try {
      if (isRefresh) {
        setIsLoading(true);
        setLastDoc(null);
      } else {
        setLoadingMore(true);
      }

      const colRef = collection(db, 'media');
      const shortTypes = ['short', 'shorts', 'minis', 'reel', 'reels'];
      let q = query(
        colRef,
        where('type', 'in', shortTypes),
        where('status', '==', 'published'),
        where('isDeleted', '==', false),
        orderBy('createdAt', 'desc'),
        limit(10)
      );
      if (!isRefresh && lastDoc) {
        q = query(
          colRef,
          where('type', 'in', shortTypes),
          where('status', '==', 'published'),
          where('isDeleted', '==', false),
          orderBy('createdAt', 'desc'),
          startAfter(lastDoc),
          limit(10)
        );
      }

      const snapshot = await getDocs(q);
      const docs = snapshot.docs;
      if (docs.length > 0) {
        setLastDoc(docs[docs.length - 1]);
      }
      setHasMore(docs.length >= 10);
      setTotalShortsCount(prev => Math.max(prev, snapshot.size + shortsList.length));

      const fetched = docs
        .map((docSnap): ShortVideo | null => {
          const item = docSnap.data();
          const normalizedType = String(item.type || '').trim().toLowerCase();
          const isShort = ['short', 'shorts', 'minis', 'reel', 'reels'].includes(normalizedType) || !item.type;
          if (!isShort && item.type) return null;

          return {
            id: docSnap.id,
            ownerUid: item.ownerUid || item.uploaderId || item.userId,
            authorName: item.authorName || 'Clue200 Creator',
            authorUsername: item.authorUsername || 'creator',
            authorAvatar: item.authorAvatar || 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?w=150',
            clueId: item.clueId,
            videoUrl: item.mediaUrl || item.storagePath,
            thumbnailUrl: item.thumbnailUrl || item.mediaUrl || 'https://images.unsplash.com/photo-1611162617474-5b21e879e113?w=500',
            caption: item.title || item.caption || 'MiniS Reel',
            audioTrack: 'Original Audio - Clue200',
            likes: item.likeCount || item.likes || 0,
            comments: item.commentCount || 0,
            shares: item.shareCount || 0,
            isLiked: false,
            isBookmarked: false,
            isFollowing: false,
            uploadedAt: item.createdAt?.toDate?.()?.toLocaleString() || item.uploadDate || 'Just now'
          };
        })
        .filter((v): v is ShortVideo => v !== null && Boolean(v.videoUrl));

      const localShorts: ShortVideo[] = (mediaHistory || [])
        .filter(item => item && (item.type === 'short' || item.type === 'video'))
        .map(item => ({
          id: item.id || Math.random().toString(),
          ownerUid: item.userId || '',
          authorName: 'You',
          authorUsername: 'you',
          authorAvatar: currentUser?.photoURL || '',
          videoUrl: item.mediaUrl,
          thumbnailUrl: item.thumbnailUrl || item.mediaUrl,
          caption: item.title || 'Untitled short',
          audioTrack: 'Original Audio - Clue200',
          likes: item.likes || 0,
          comments: 0,
          shares: 0,
          isLiked: false,
          isBookmarked: false,
          isFollowing: false,
          uploadedAt: item.uploadDate || 'Just now'
        }));

      // Deduplicate by video ID
      const map = new Map<string, ShortVideo>();
      const rawBatch = isRefresh ? [...localShorts, ...fetched, ...sampleShorts] : [...shortsList, ...localShorts, ...fetched];
      rawBatch.forEach(s => {
        if (s && s.id && !map.has(s.id)) {
          map.set(s.id, s);
        }
      });

      const uniqueList = Array.from(map.values());
      const prioritizedList = prioritizeAndRandomizeFeed(
        uniqueList,
        followingUidsRef.current,
        video => video.ownerUid,
        uniqueList.length
      );
      setShortsList(prioritizedList);
      setIsLoading(false);
      setLoadingMore(false);
    } catch (err) {
      console.error('Error fetching MiniS batch:', err);
      setIsLoading(false);
      setLoadingMore(false);
      if (shortsList.length === 0) {
        setShortsList(sampleShorts);
      }
    }
  };

  useEffect(() => {
    void fetchShortsBatch(true);
  }, []);

  const filteredShorts = shortsList.filter(s => {
    if (s.ownerUid && blockedUserIds.has(s.ownerUid)) return false;
    if (!searchQuery) return true;
    const q = searchQuery.toLowerCase();
    return (s.caption || '').toLowerCase().includes(q) || (s.authorName || '').toLowerCase().includes(q);
  });

  const [pullDistance, setPullDistance] = useState(0);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const touchStartY = useRef(0);
  const isDragging = useRef(false);

  const handleTouchStart = (e: React.TouchEvent<HTMLDivElement>) => {
    if (isRefreshing) return;
    if (containerRef.current && containerRef.current.scrollTop <= 2) {
      touchStartY.current = e.touches[0].clientY;
      isDragging.current = true;
    } else {
      isDragging.current = false;
    }
  };

  const handleTouchMove = (e: React.TouchEvent<HTMLDivElement>) => {
    if (!isDragging.current || isRefreshing) return;
    if (containerRef.current && containerRef.current.scrollTop > 2) {
      isDragging.current = false;
      setPullDistance(0);
      return;
    }
    const diff = e.touches[0].clientY - touchStartY.current;
    if (diff > 0) {
      setPullDistance(Math.min(90, diff * 0.4));
    } else {
      setPullDistance(0);
    }
  };

  const handleTouchEnd = async () => {
    if (!isDragging.current) return;
    isDragging.current = false;
    if (pullDistance >= 60 && !isRefreshing) {
      setIsRefreshing(true);
      setPullDistance(45);
      try {
        await fetchShortsBatch(true);
      } catch (err) {
        console.warn('MiniS refresh failed:', err);
      } finally {
        setIsRefreshing(false);
        setPullDistance(0);
      }
    } else {
      setPullDistance(0);
    }
  };

  if (blockedUsersError) return <div role="alert" className="flex h-full items-center justify-center bg-black p-6 text-center text-sm text-rose-300">Could not load your block list. MiniS content is hidden until it is available.</div>;

  if (isLoading || !blockedUsersReady) {
    return (
      <div className="h-full w-full bg-black flex flex-col gap-4 p-4">
        <Skeleton className="w-full h-full bg-slate-900 rounded-2xl" />
      </div>
    );
  }

  return (
    <div
      id="minis-container"
      ref={containerRef}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
      onScroll={e => {
        const target = e.currentTarget;
        // Prefetch when near end (last 3 items)
        if (target.scrollHeight - target.scrollTop <= target.clientHeight + 600 && !loadingMore && hasMore) {
          void fetchShortsBatch(false);
        }
      }}
      className="relative h-[calc(100dvh-56px)] w-full overflow-y-scroll snap-y snap-mandatory no-scrollbar bg-black scroll-smooth select-none"
    >
      {/* Pull To Refresh Indicator Overlay */}
      {pullDistance > 0 && (
        <div
          style={{ height: `${pullDistance}px` }}
          className="absolute top-0 left-0 right-0 z-40 flex items-center justify-center bg-black/70 backdrop-blur-md pointer-events-none transition-all"
        >
          <div className="flex items-center gap-2 text-white text-xs font-bold px-3.5 py-1.5 rounded-full bg-slate-900/90 border border-slate-700 shadow-xl">
            {isRefreshing ? (
              <>
                <LoaderCircle className="w-4 h-4 animate-spin text-cyan-400" />
                <span>Updating MiniS…</span>
              </>
            ) : pullDistance >= 60 ? (
              <>
                <ArrowDown className="w-4 h-4 rotate-180 transition-transform text-cyan-400" />
                <span className="text-cyan-400">Release to refresh</span>
              </>
            ) : (
              <>
                <ArrowDown className="w-4 h-4 text-slate-400" />
                <span className="text-slate-300">Pull down to refresh</span>
              </>
            )}
          </div>
        </div>
      )}

      {/* TOP BAR OVERLAY */}
      <div className="absolute top-4 right-4 z-30 flex items-center gap-3 pointer-events-auto">
        <button
          onClick={() => setShowSearchModal(true)}
          className="w-10 h-10 rounded-full bg-black/40 backdrop-blur-md flex items-center justify-center text-white border border-white/15 shadow-xl hover:bg-black/60 transition-all active:scale-95"
          title="Search MiniS"
        >
          <Search className="w-5 h-5 text-white" />
        </button>
      </div>

      {filteredShorts.map((short, index) => (
        <ShortItem
          key={`${short.id}-${index}`}
          short={short}
          isMuted={isMuted}
          onToggleMute={() => setIsMuted(!isMuted)}
          onOpenAuth={onOpenAuth}
          currentUser={currentUser}
          onOpenProfile={onOpenProfile}
          onDeleted={id => setShortsList(previous => previous.filter(item => item.id !== id))}
          isNearEnd={index >= filteredShorts.length - 3 && hasMore}
          onLoadMore={() => {
            if (!loadingMore && hasMore) void fetchShortsBatch(false);
          }}
        />
      ))}

      {hasMore ? (
        <div className="h-40 snap-start flex items-center justify-center bg-black text-xs font-bold text-white/50">
          Loading more MiniS...
        </div>
      ) : (
        <div className="h-40 snap-start flex items-center justify-center bg-black text-xs font-bold text-white/70">
          You're all caught up
        </div>
      )}

      {/* Search Modal */}
      {showSearchModal && (
        <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md p-4 flex flex-col justify-start pt-12 animate-in fade-in">
          <div className="bg-white rounded-3xl max-w-sm w-full mx-auto p-4 shadow-2xl space-y-3">
            <div className="flex items-center justify-between pb-2 border-b border-slate-100">
              <h3 className="font-black text-xs uppercase tracking-wider">Search MiniS</h3>
              <button onClick={() => setShowSearchModal(false)} className="text-slate-400 p-1">✕</button>
            </div>
            <div className="relative">
              <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                type="text"
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                placeholder="Search reels or creators..."
                className="w-full text-xs pl-9 pr-3 py-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:outline-none"
                autoFocus
              />
            </div>
            <button
              onClick={() => setShowSearchModal(false)}
              className="w-full py-2.5 bg-blue-900 text-white rounded-xl text-xs font-black uppercase tracking-widest shadow-md"
            >
              Done
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

// Individual MiniS Item Component
const ShortItem: React.FC<{
  short: ShortVideo;
  isMuted: boolean;
  onToggleMute: () => void;
  onOpenAuth?: () => void;
  currentUser?: AppUser | null;
  onOpenProfile?: (uid: string) => void;
  onDeleted?: (id: string) => void;
  isNearEnd: boolean;
  onLoadMore: () => void;
}> = ({
  short,
  isMuted,
  onToggleMute,
  onOpenAuth,
  currentUser,
  onOpenProfile,
  onDeleted,
  isNearEnd,
  onLoadMore
}) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [showPlayIconAnim, setShowPlayIconAnim] = useState(false);
  const [showHeartAnim, setShowHeartAnim] = useState(false);
  const [showComments, setShowComments] = useState(false);
  const [isLiked, setIsLiked] = useState(short.isLiked);
  const [likesCount, setLikesCount] = useState(short.likes);
  const [isSaved, setIsSaved] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [followBusy, setFollowBusy] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [seekBarProgress, setSeekBarProgress] = useState(0);

  const videoRef = useRef<HTMLVideoElement>(null);
  const itemRef = useRef<HTMLDivElement>(null);
  const viewCounted = useRef(false);
  const lastTapRef = useRef(0);

  useEffect(() => {
    markCacheActive(short.videoUrl);
    return () => releaseCacheActive(short.videoUrl);
  }, [short.videoUrl]);

  useEffect(() => {
    if (!currentUser || !short.ownerUid) return;
    return subscribeToFollowStatus(currentUser.uid, short.ownerUid, setIsFollowing);
  }, [currentUser?.uid, short.ownerUid]);

  // PART 1 & 2: Intersection Observer for Auto-play & Preload
  useEffect(() => {
    let timer: any;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsPlaying(true);
          videoRef.current?.play().catch(() => {});
          if (!viewCounted.current) {
            timer = setTimeout(() => {
              incrementMediaCount(short.id, 'viewCount');
              viewCounted.current = true;
            }, 3000);
          }
          if (isNearEnd) {
            onLoadMore();
          }
        } else {
          setIsPlaying(false);
          videoRef.current?.pause();
          if (timer) clearTimeout(timer);
        }
      },
      { threshold: 0.75 }
    );
    if (itemRef.current) observer.observe(itemRef.current);
    return () => {
      observer.disconnect();
      if (timer) clearTimeout(timer);
    };
  }, [short.id, isNearEnd, onLoadMore]);

  const handleLike = async () => {
    if (!currentUser) {
      onOpenAuth?.();
      return;
    }
    if (isLiked) return;
    const liked = await likeMediaOnce(short.id, currentUser.uid);
    if (!liked) return;
    setIsLiked(true);
    setLikesCount(prev => prev + 1);
  };

  const handleDoubleTapLike = (e: React.MouseEvent) => {
    const now = Date.now();
    const DOUBLE_TAP_DELAY = 300;
    if (now - lastTapRef.current < DOUBLE_TAP_DELAY) {
      // Double tap detected -> Like with heart animation
      setShowHeartAnim(true);
      setTimeout(() => setShowHeartAnim(false), 800);
      void handleLike();
    } else {
      // Single tap -> Pause/Play with fade animation
      if (videoRef.current) {
        if (videoRef.current.paused) {
          void videoRef.current.play();
          setIsPlaying(true);
        } else {
          videoRef.current.pause();
          setIsPlaying(false);
        }
        setShowPlayIconAnim(true);
        setTimeout(() => setShowPlayIconAnim(false), 700);
      }
    }
    lastTapRef.current = now;
  };

  const handleFollowClick = async () => {
    if (!currentUser) {
      onOpenAuth?.();
      return;
    }
    if (!short.ownerUid || followBusy) return;
    const wasFollowing = isFollowing;
    setIsFollowing(!wasFollowing);
    setFollowBusy(true);
    try {
      if (wasFollowing) await unfollowUser(short.ownerUid, currentUser);
      else await followUser(short.ownerUid, currentUser, short.authorName);
    } catch (error) {
      console.error('Could not update short follow state:', error);
      setIsFollowing(wasFollowing);
    } finally {
      setFollowBusy(false);
    }
  };

  const isSelfVideo = currentUser && short.ownerUid === currentUser.uid;

  return (
    <div
      ref={itemRef}
      className="short-item-wrapper h-full w-full snap-start relative bg-slate-950 flex flex-col justify-between select-none overflow-hidden"
    >
      {/* Video Container (object-fit: cover for portrait, fit + black bars for landscape, with thumbnail poster) */}
      <div
        className="absolute inset-0 z-0 cursor-pointer flex items-center justify-center bg-black"
        onClick={handleDoubleTapLike}
      >
        <video
          ref={videoRef}
          src={short.videoUrl}
          poster={short.thumbnailUrl}
          loop
          muted={isMuted}
          playsInline
          onTimeUpdate={() => {
            if (videoRef.current && videoRef.current.duration) {
              setSeekBarProgress((videoRef.current.currentTime / videoRef.current.duration) * 100);
            }
          }}
          className="w-full h-full object-cover aspect-[9/16] brightness-[0.92]"
        />

        <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black/80 pointer-events-none" />

        {/* Center Play/Pause Fade Animation Icon */}
        {showPlayIconAnim && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none animate-in fade-in zoom-in duration-200">
            <div className="w-16 h-16 rounded-full bg-black/60 text-white flex items-center justify-center backdrop-blur-md border border-white/20 shadow-2xl">
              {isPlaying ? <Play className="w-7 h-7 fill-white translate-x-0.5" /> : <Pause className="w-7 h-7" />}
            </div>
          </div>
        )}

        {/* Double Tap Heart Animation */}
        {showHeartAnim && (
          <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <Heart className="w-24 h-24 text-rose-500 fill-rose-500 animate-in zoom-in-75 fade-in duration-300 drop-shadow-2xl" />
          </div>
        )}
      </div>

      {/* PART 4: RIGHT SIDE VERTICAL ACTION RAIL */}
      <div className="absolute bottom-24 right-3 z-20 flex flex-col items-center gap-5">
        {/* 1) LIKE */}
        <button onClick={handleLike} className="flex flex-col items-center gap-1 group cursor-pointer">
          <div className="w-11 h-11 rounded-full bg-black/40 backdrop-blur-xl flex items-center justify-center border border-white/20 text-white shadow-xl active:scale-125 transition-transform">
            <Heart className={`w-5.5 h-5.5 ${isLiked ? 'text-rose-500 fill-rose-500' : 'text-white'}`} />
          </div>
          <span className="text-[11px] font-bold text-white drop-shadow-md">{formatCount(likesCount)}</span>
        </button>

        {/* 2) COMMENTS */}
        <button onClick={() => { if (!currentUser) onOpenAuth?.(); else setShowComments(true); }} className="flex flex-col items-center gap-1 group cursor-pointer">
          <div className="w-11 h-11 rounded-full bg-black/40 backdrop-blur-xl flex items-center justify-center border border-white/20 text-white shadow-xl">
            <MessageCircle className="w-5.5 h-5.5 text-white" />
          </div>
          <span className="text-[11px] font-bold text-white drop-shadow-md">{formatCount(short.comments)}</span>
        </button>

        {/* 3) SAVE */}
        <button onClick={() => setIsSaved(!isSaved)} className="flex flex-col items-center gap-1 group cursor-pointer">
          <div className="w-11 h-11 rounded-full bg-black/40 backdrop-blur-xl flex items-center justify-center border border-white/20 text-white shadow-xl">
            <Bookmark className={`w-5 h-5 ${isSaved ? 'text-amber-400 fill-amber-400' : 'text-white'}`} />
          </div>
          <span className="text-[10px] font-bold text-white drop-shadow-md">Save</span>
        </button>

        {/* 4) SHARE */}
        <button
          onClick={() => void shareContent('short', short.id, short.caption || 'Clue200 MiniS')
            .then(() => incrementMediaCount(short.id, 'shareCount'))
            .catch(error => { if (error?.name !== 'AbortError') console.error('Share failed:', error); })}
          className="flex flex-col items-center gap-1 group cursor-pointer"
        >
          <div className="w-11 h-11 rounded-full bg-black/40 backdrop-blur-xl flex items-center justify-center border border-white/20 text-white shadow-xl">
            <Share2 className="w-5.5 h-5.5 text-white" />
          </div>
          <span className="text-[10px] font-bold text-white drop-shadow-md">Share</span>
        </button>

        <ContentActionsMenu
          contentId={short.id}
          contentType="short"
          contentOwnerUid={short.ownerUid}
          contentTitle={short.caption}
          currentUser={currentUser}
          onOpenAuth={onOpenAuth}
          onDeleted={onDeleted}
        />
      </div>

      {/* PART 4: BOTTOM-LEFT METADATA & FOLLOW ROW */}
      <div className="absolute bottom-5 left-0 right-16 z-20 px-4 text-white space-y-2.5">
        <div className="flex items-center gap-2.5 flex-wrap">
          <div
            className="flex items-center gap-2 cursor-pointer group"
            onClick={() => short.ownerUid && onOpenProfile?.(short.ownerUid)}
          >
            <img
              src={short.authorAvatar}
              alt={short.authorName}
              className="w-9 h-9 rounded-full object-cover border border-white/30 shadow-md group-hover:scale-105 transition-transform"
            />
            <span className="font-black text-xs text-white drop-shadow-md">
              @{short.authorUsername}
            </span>
          </div>

          <ClueBadge id={short.clueId} />
          <CheckCircle2 className="w-3.5 h-3.5 text-blue-400 fill-blue-400 shrink-0" />

          {!isSelfVideo && (
            <button
              onClick={() => void handleFollowClick()}
              disabled={followBusy}
              className={`px-3.5 py-1.5 rounded-full text-[10px] font-black uppercase tracking-wider transition-all shadow-md ${
                isFollowing
                  ? 'bg-white/20 text-white/80 backdrop-blur-md cursor-default'
                  : 'bg-blue-600 text-white shadow-blue-600/30 active:scale-95'
              }`}
            >
              {isFollowing ? <><UserCheck className="inline w-3 h-3 mr-0.5" />Following</> : 'Follow'}
            </button>
          )}
        </div>

        {/* Video Title (Expandable on tap) */}
        <div
          onClick={() => setIsExpanded(!isExpanded)}
          className="cursor-pointer bg-black/20 backdrop-blur-xs p-2 rounded-xl border border-white/10"
        >
          <p className={`text-xs text-white/95 font-medium leading-snug drop-shadow-md ${isExpanded ? '' : 'line-clamp-2'}`}>
            {short.caption}
          </p>
          {!isExpanded && short.caption && short.caption.length > 70 && (
            <span className="text-[10px] text-blue-300 font-bold ml-1">more</span>
          )}
        </div>
      </div>

      {/* PART 1: SEEKBAR & PROGRESS BAR (Under notes with time display) */}
      <div className="absolute bottom-0 left-0 right-0 z-30 flex flex-col pointer-events-auto">
        <div className="flex items-center justify-between px-3 pb-0.5 text-[9px] font-mono font-bold text-white/80 drop-shadow-md pointer-events-none">
          <span>
            {videoRef.current?.currentTime ? `${Math.floor(videoRef.current.currentTime / 60)}:${String(Math.floor(videoRef.current.currentTime % 60)).padStart(2, '0')}` : '0:00'}
          </span>
          <span>
            {videoRef.current?.duration ? `${Math.floor(videoRef.current.duration / 60)}:${String(Math.floor(videoRef.current.duration % 60)).padStart(2, '0')}` : '0:00'}
          </span>
        </div>
        <div className="relative w-full h-1.5 bg-white/20 backdrop-blur-xs cursor-pointer hover:h-2.5 transition-all">
          <input
            type="range"
            min={0}
            max={100}
            value={seekBarProgress}
            onChange={e => {
              const val = Number(e.target.value);
              setSeekBarProgress(val);
              if (videoRef.current && videoRef.current.duration) {
                videoRef.current.currentTime = (val / 100) * videoRef.current.duration;
              }
            }}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10"
          />
          <div
            className="h-full bg-gradient-to-r from-blue-500 via-rose-500 to-amber-400 rounded-r-full shadow-md"
            style={{ width: `${seekBarProgress}%` }}
          />
        </div>
      </div>

      {/* Comments Bottom Sheet */}
      <InstagramComments
        isOpen={showComments}
        onClose={() => setShowComments(false)}
        mediaId={short.id}
        mediaType="short"
        currentUser={currentUser || null}
        onOpenAuth={onOpenAuth || (() => {})}
        authorName={short.authorName}
        likesCount={likesCount}
        timeAgo={short.uploadedAt}
        onOpenProfile={onOpenProfile}
      />
    </div>
  );
};
